import React, { useState } from 'react';
import Async from 'react-select/async';
import { components } from 'react-select';
import {
	useFloating,
	useInteractions,
	useClick,
	useDismiss,
	useRole,
	autoUpdate,
	offset,
	flip,
	shift,
	FloatingFocusManager,
	FloatingPortal
} from '@floating-ui/react';
import { tagStore } from 'components/settings/tags/TagStore';

const Option = props => (
	<div>
		<components.Option {...props}>
			<input type="checkbox" checked={props.isSelected} onChange={() => null} />{' '}
			<label>{props.label}</label>
		</components.Option>
	</div>
);

const selectStyles = {
	option: () => ({
		padding: 12,
		textAlign: 'left',
		color: '#101323',
		fontFamily: 'Manrope',
		fontSize: 14,
		fontWeight: 400,
		display: 'flex',
		alignItems: 'center',
		gap: 8
	}),
	menu: () => ({
		backgroundColor: '#FFFFFF'
	})
};

export default function TagsDropdown({
	target,
	value,
	onChange,
	placeholder = 'Tags',
	optionsFilter = options => options
}) {
	const [isOpen, setIsOpen] = useState(false);

	// Floating UI setup
	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [offset(8), flip(), shift()],
		whileElementsMounted: autoUpdate,
		placement: 'bottom-start'
	});

	// Interaction hooks
	const click = useClick(context, {
		preventScroll: true
	});
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role
	]);

	const loadOptions = inputValue => {
		return new Promise(resolve => {
			resolve(getTags(inputValue));
		});
	};

	const getTags = async inputValue => {
		inputValue = inputValue.trim();
		const result = await tagStore.getTags(inputValue);
		return optionsFilter(result.data);
	};

	const noOptionsMessage = value => {
		if (value.inputValue && value.inputValue.length >= 1) {
			return 'No matches found';
		} else {
			return 'Enter text to search tags';
		}
	};

	const getOptionValue = option => option.id;

	const getOptionLabel = option => `${option.name}`;

	return (
		<>
			{target({
				ref: refs.setReference,
				...getReferenceProps()
			})}

			<FloatingPortal>
				{isOpen && (
					<FloatingFocusManager context={context} modal={false}>
						<div
							ref={refs.setFloating}
							style={floatingStyles}
							{...getFloatingProps()}
							className="tags-dropdown-container">
							<Async
								classNamePrefix="tags-dropdown"
								tabSelectsValue={false}
								controlShouldRenderValue={false}
								closeMenuOnSelect={false}
								isClearable={false}
								isMulti
								menuIsOpen
								components={{
									Option,
									IndicatorSeparator: null,
									DropdownIndicator: null
								}}
								placeholder={placeholder}
								cacheOptions
								defaultOptions
								loadOptions={loadOptions}
								getOptionValue={getOptionValue}
								getOptionLabel={getOptionLabel}
								noOptionsMessage={noOptionsMessage}
								hideSelectedOptions={false}
								backspaceRemovesValue={false}
								onChange={e => onChange(e)}
								value={value}
								menuPlacement="auto"
								styles={selectStyles}
							/>
						</div>
					</FloatingFocusManager>
				)}
			</FloatingPortal>
		</>
	);
}
